import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { PriorityDot } from "@/components/shared/PriorityDot";
import { BusinessStage } from "@/components/shared/BusinessStage";
import { Loading } from "@/components/shared/Loading";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { useExceptions, useMe, useAction, useRole } from "@/api/hooks";
import { toast } from "sonner";
import { AlertTriangle, Check, ArrowRight } from "lucide-react";

const JOB_DETAIL_ROLES = ["sales", "operations", "finance", "management"];

function recordTarget(exception, role) {
  if (exception.financeHoldId) {
    const acceptanceId = exception.acceptanceId || exception.linkedAcceptanceId || exception.objectId;
    if (!acceptanceId) return null;
    return { label: "Order", id: acceptanceId, path: role === "finance" ? `/finance/orders/${acceptanceId}` : null };
  }
  const jobId = exception.jobId || (exception.objectType === "job" ? exception.objectId : null);
  if (!jobId) return null;
  return { label: "Job", id: jobId, path: JOB_DETAIL_ROLES.includes(role) ? `/${role}/jobs/${jobId}` : null };
}

export default function Exceptions() {
  const navigate = useNavigate();
  const { data: exceptions } = useExceptions();
  const { data: me } = useMe();
  const role = useRole();
  const act = useAction(["exceptions"]);
  const [selected, setSelected] = useState(null);
  const [resolution, setResolution] = useState("");
  if (!exceptions || !me) return <Loading />;

  const resolveScope = me.caps.resolve_exception;
  const canResolve = (e) => !e.financeHoldId && (resolveScope === "any" || resolveScope === e.domain);

  const resolve = () => act.mutate({ url: `/exceptions/${selected.id}/resolve`, body: { resolution: resolution.trim(), expectedVersion: selected.version || 0 } }, { onSuccess: () => { toast.success("Exception resolved"); setSelected(null); setResolution(""); }, onError: (error) => toast.error(error.response?.data?.message || error.response?.data?.detail || "Exception could not be resolved") });

  return (
    <>
      <PageHeader title="Exceptions" subtitle="Freight is exception-heavy — the system surfaces what needs action" />
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {exceptions.map((e) => {
          const resolved = String(e.status).toUpperCase() === "RESOLVED";
          const target = recordTarget(e, role);
          const displayType = e.type || e.title || "Exception";
          return (
            <div key={e.id} data-testid={`exception-${e.id}`} className={`rounded-xl border bg-white p-4 ${resolved ? "border-slate-200 opacity-70" : e.severity === "P2" ? "border-red-200" : "border-amber-200"}`}>
              <div className="flex items-start gap-3">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${resolved ? "bg-emerald-50" : "bg-red-50"}`}>{resolved ? <Check className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-red-500" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <PriorityDot priority={e.severity} />
                    <span className="font-display font-semibold text-slate-800">{displayType}</span>
                    <StatusPill status={resolved ? "Complete" : displayType} bucket={resolved ? "success" : undefined} />
                    <span className="text-[10px] font-medium text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 capitalize">{e.domain}</span>
                  </div>
                  <div className="text-[13px] text-slate-600 mt-1">{e.note || e.title || e.description || "Review the exception details and supporting record."}</div>
                  <div className="text-[11px] text-slate-400 mt-1">{target && <>{target.label} {target.path ? <button onClick={() => navigate(target.path)} className="font-mono-num text-indigo-600 hover:underline">{target.id}</button> : <span className="font-mono-num">{target.id}</span>} · </>}{e.customer && <>{e.customer} · </>}{e.owner && <>Owner {e.owner} · </>}{e.nextUpdate && <>Next update {e.nextUpdate}</>}</div>
                  {e.businessStage && (
                    <div className="mt-2 flex min-w-0 items-center gap-2">
                      <BusinessStage stage={e.businessStage} compact />
                      <span className="truncate text-[11px] text-slate-500">{e.businessStage.name} · Last status: {e.businessStage.lastStatus}</span>
                    </div>
                  )}
                </div>
                {!resolved && (
                  <div className="shrink-0 flex flex-col gap-1.5">
                    {e.financeHoldId ? (
                      target?.path
                        ? <Button size="sm" variant="outline" className="h-8" onClick={() => navigate(target.path)}>Review hold <ArrowRight className="h-3 w-3 ml-1" /></Button>
                        : <span className="text-[11px] text-slate-400 max-w-[150px] text-right">Finance resolves this in Order Review</span>
                    ) : canResolve(e) ? <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => setSelected(e)} data-testid={`exception-resolve-${e.id}`}><Check className="h-3.5 w-3.5 mr-1" />Resolve</Button>
                      : <span className="text-[11px] text-slate-400 max-w-[130px] text-right">{e.domain} team resolves this</span>}
                    {!e.financeHoldId && target?.path && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate(target.path)}>Open job <ArrowRight className="h-3 w-3 ml-1" /></Button>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Dialog open={!!selected} onOpenChange={(open) => { if (!open && !act.isPending) { setSelected(null); setResolution(""); } }}><DialogContent className="sm:max-w-lg"><DialogHeader><DialogTitle>Resolve exception</DialogTitle><DialogDescription>Describe what was corrected and identify the evidence or decision used. This note is retained in the audit trail.</DialogDescription></DialogHeader><Textarea rows={4} value={resolution} onChange={(event) => setResolution(event.target.value)} placeholder="Resolution and evidence reference" data-testid="exception-resolution" /><DialogFooter><Button variant="outline" onClick={() => setSelected(null)} disabled={act.isPending}>Cancel</Button><Button onClick={resolve} disabled={act.isPending || !resolution.trim()} data-testid="exception-resolution-submit">Confirm resolution</Button></DialogFooter></DialogContent></Dialog>
    </>
  );
}
